import { useEffect, useRef, useState } from "react";
import {
  camelCaseToWords,
  firstLetterToLower,
  transformValue,
  navigateTo,
  getCorrectHoursType,
  iconEditEnabled,
  iconDeleteEnabled,
  getUserNameFromArray,
  isProjectClosed,
  isClosed,
  getTimeEntryTypeLabel,
} from "../utils/timesUtils";
import { useDispatch, useSelector } from "react-redux";
import {
  ROUTE_APPROVE_TIME_TIMESHEETS,
  THE_HUB,
  BILLABLE,
  nonBillableIds,
  LOCKED,
  PROJ_UNPAID_TIME_OFF_ID,
} from "../../SharedModule/utils/constants";
import {
  dateFormatEntryEdit,
  decodeURIComponentSafe,
  nameDayDateFormat,
  quantityFormat,
} from "../../SharedModule/utils/formatters";
import { SummaryTotals } from "../components/SummaryTotals";
import {
  BiLeftArrowAlt,
  BiLockAlt,
  BiInfoCircle,
  BiEditAlt,
  BiCalendar,
  BiTrash,
} from "react-icons/bi";
import {
  resetFilters,
  SelectOrderedBy,
  setFilters,
} from "../redux/reducers/approvalTime.reducer";
import { useApproveTimeByUser } from "../hooks/useApproveTimeByUser";
import { DayTotals } from "../components/DayTotals";
import Footer from "../../SharedModule/components/Footer";
import { TimeEntryApprovalTime } from "../types/times-module-types";
import { TimesheetsService } from "../services/timesheets";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import { ModalPortal } from "../../SharedModule/components/ModalPortal";
import { useMoreInfoTimeEntryModal } from "../hooks/useMoreInfoTimeEntryModal";
import { ModalTimeEntry } from "../components/ModalTimeEntry";
import { useDeleteTimeEntryModal } from "../hooks/useDeleteTimeEntryModal";
import { useTopNavigationInfo } from "../../SharedModule/hooks/useTopNavigationInfo";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { ActionsButton } from "../components/ActionsButton";
import { useBulkTimeEntryActionModals } from "../hooks/useBulkTimeEntryActionModals";
import {
  setNextPersonIndex,
  setLoading,
  setSearchState,
} from "../redux/reducers/approveTimeByUser.reducer";
import { CheckSkeleton } from "../../SharedModule/utils/checkSkeleton";
import { Link, useNavigate } from "react-router";
import { Spinner } from "../../SharedModule/components/Spinner";
import {
  getDateFromISOFormat,
  getLastWorkableDay,
  getPreviousWorkableDay,
} from "../../SharedModule/utils/dateUtils";
import { ExternalLink } from "../../SharedModule/components/ExternalLink";

export const ApproveTimeByUserPage = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [entriesSelected, setEntriesSelected] = useState<any>([]);
  const { showMoreInfo, moreInfoModalPortal } = useMoreInfoTimeEntryModal();

  const [activeEntry, setActiveEntry] = useState<TimeEntryApprovalTime>(Object);
  const [timeEntriesListByUser, setTimeEntriesListByUser] = useState([]);

  // Imported to use, when edit or delete a time entry from actual user, should update hours in header
  const { loadInfo } = useTopNavigationInfo();

  let approveAndViewNextClicked: boolean = false;
  let actualNextIndex: number = -1;

  const today = new Date();
  let lastWorkableDay: Date = getLastWorkableDay();

  const {
    isTimeAdmin,
    isSuperTimeAdmin,
    isSupervisorOrPO,
    isDeliveryTimeAdmin,
    summary,
    isLoading,
    rolesLoaded,
    timeEntriesList,
    activeUserId,
    holidays,
    selectProjectOrWorkOrder,
    MetaTimesInfo,
    searchState,
    initialFilters,
    nextPersonIndex,
    loadPageInfo,
  } = useApproveTimeByUser();

  let previousWorkableDay = getPreviousWorkableDay(1, holidays);

  // When user is not admin, should not see PROJ_UNPAID_TIME_OFF_ID - ASUITE11-2464
  let selectProjectOrWorkOrderAfterFilter = selectProjectOrWorkOrder;
  if (!isTimeAdmin && selectProjectOrWorkOrder[1].options.length > 0) {
    selectProjectOrWorkOrderAfterFilter[1].options =
      selectProjectOrWorkOrder[1].options.filter(
        (project) => project.entityId !== PROJ_UNPAID_TIME_OFF_ID
      );
  }

  let listFiltersTouched: any[] = [];
  if (searchState?.filters) {
    for (const property in searchState.filters) {
      if (searchState.filters[property]?.length > 0 && property !== "endDate") {
        listFiltersTouched.push({
          name: camelCaseToWords(property),
          value: transformValue(property, searchState.filters[property]),
        });
      }
    }
  }

  const { userList } = useSelector(SelectOrderedBy);

  useEffect(() => {
    document.title = "The Hub - Approve Time By User";
    // to avoid issue on refresh from browser
    if (!searchState.user) {
      navigate(ROUTE_APPROVE_TIME_TIMESHEETS);
    }

    if (initialFilters !== searchState.filters && searchState.user !== null) {
      dispatch(setFilters(searchState.filters));
    }

    return () => {
      dispatch(setNextPersonIndex(-1));
      // reset filters if user got to another page
      if (globalThis.location.pathname !== ROUTE_APPROVE_TIME_TIMESHEETS) {
        //   clean filters
        dispatch(resetFilters());
      }
    };
  }, []);

  const isSelected = (entry: TimeEntryApprovalTime) => {
    let response = entriesSelected.indexOf(entry);
    return response > -1;
  };

  const updatedSelected = (entry: TimeEntryApprovalTime) => {
    let response = entriesSelected.indexOf(entry);
    if (response < 0) {
      const dataUpdated = [entry, ...entriesSelected];
      setEntriesSelected(dataUpdated);
    } else {
      setEntriesSelected(
        entriesSelected.filter((item) => item.id !== entry.id)
      );
    }
  };

  const updateSelectedByDay = (date: string, value: boolean) => {
    let response = timeEntriesList[date].filter(
      (entry) => entry.source === THE_HUB
    );
    if (value) {
      const dataUpdated = [...response, ...entriesSelected];
      setEntriesSelected(dataUpdated);
    } else {
      setEntriesSelected(
        entriesSelected.filter((item) => item.entryDate !== date)
      );
    }
  };

  const updateAllSelected = () => {
    let response: TimeEntryApprovalTime[] = [];
    Object.keys(timeEntriesList).forEach((keyName) => {
      timeEntriesList[keyName].forEach((element) => {
        if (element.source === THE_HUB) {
          response.push(element);
        }
      });
    });
    setEntriesSelected(response);
  };

  const getUserName = (userId: string) => {
    // to avoid issue on refresh from browser on query result page
    let actualUser = getUserNameFromArray(
      MetaTimesInfo.timeUsers ?? [],
      userId
    );
    if (MetaTimesInfo.timeUsers && actualUser) {
      return actualUser;
    } else if (searchState.user) {
      return searchState.user.userName;
    } else {
      navigate(ROUTE_APPROVE_TIME_TIMESHEETS);
    }
  };

  // Modal config
  const modalRef = useRef<undefined>(undefined);
  const showModal = () => {
    (modalRef.current as any).show(true);
  };
  const closeModal = () => {
    (modalRef.current as any).show(false);
  };

  const editEntry = (entry: TimeEntryApprovalTime) => {
    setActiveEntry({
      ...entry,
      entryDate: getDateFromISOFormat(entry.entryDate),
    });
    // select time entries by same day and same user as actual entry
    setTimeEntriesListByUser(
      timeEntriesList[entry.entryDate].filter(
        (elem) => elem.userId === entry.userId
      )
    );
    showModal();
  };

  // refresh info page, also with condition if user should be update header
  const refreshPage = () => {
    // if user is not admin or is admin and is adding own hours
    if (!isTimeAdmin || activeUserId === activeEntry.userId) {
      loadInfo(false);
    }
    loadPageInfo();
  };

  const { showDeleteModal, deleteEntryModal, deleteModalPortal } =
    useDeleteTimeEntryModal(
      activeEntry,
      isSuperTimeAdmin,
      () => dispatch(setLoading(true)),
      refreshPage
    );

  const deleteEntry = (entry: TimeEntryApprovalTime) => {
    setActiveEntry(entry);
    showDeleteModal();
  };

  const loadBodyToupdateTimeEntries = (action: number, value: any) => {
    // 1-billable 2-nonBillable 3-approved 4-unapprove 5=moveDate 6-moveProject
    let body = {
      timeEntriesIds:
        action !== 3
          ? entriesSelected.map((elem) => elem.timeEntryId)
          : entriesSelected
              .filter((elem) => elem.categoryId !== 3)
              .map((elem) => elem.timeEntryId),
      action: {
        id: action,
        nonBillableReasonId: action === 2 ? value : null,
        entryDate: action === 5 ? value : null,
        entityId: action === 6 ? value.entityId : null,
        categoryId: action === 6 ? value.categoryId : null,
      },
    };
    return body;
  };

  const logicAfterApprove = () => {
    if (userList) {
      // check what button was clicked: "Approve" or "Approve & view next person"
      if (approveAndViewNextClicked) {
        if (actualNextIndex > -1) {
          dispatch(
            setSearchState({
              user: userList[actualNextIndex],
              filters: searchState.filters,
              hoursType: searchState.hoursType,
            })
          );
          approveAndViewNextClicked = false;
          dispatch(setNextPersonIndex(actualNextIndex));
        } else {
          // Should go back to approve time page
          dispatch(setNextPersonIndex(-1));
          navigate(ROUTE_APPROVE_TIME_TIMESHEETS);
        }
      } else {
        dispatch(setNextPersonIndex(-1));
        loadPageInfo();
      }

      // clean entries selected
      setEntriesSelected([]);
    }
  };

  const updateEntriesSelected = async (action: number, value: any) => {
    // set loading to true
    dispatch(setLoading(true));

    // close modals
    if (action === 5) {
      closeActionChangeDateModal();
    } else if (action === 6) {
      closeActionChangeProjectModal();
    } else if (action === 2) {
      closeActionNonBillaleModal();
    }

    let body = loadBodyToupdateTimeEntries(action, value);

    if (body.timeEntriesIds.length > 0) {
      await TimesheetsService.updateTimeEntries(body)
        .then((response) => {
          if (body.timeEntriesIds.length < entriesSelected.length) {
            dispatch(
              addToast({
                mode: "warning",
                message: "Time is approved when the Work Order is Closed",
              })
            );
          }
          dispatch(
            addToast({ mode: "success", message: response.data.message })
          );
          logicAfterApprove();
        })
        .catch((error) => {
          dispatch(addToast({ mode: "error", message: error }));
        });
    } else {
      dispatch(
        addToast({
          mode: "warning",
          message: "Time is approved when the Work Order is Closed",
        })
      );
      logicAfterApprove();
    }
  };

  const {
    showActionNonBillaleModal,
    closeActionNonBillaleModal,
    showActionChangeDateModal,
    closeActionChangeDateModal,
    showActionChangeProjectModal,
    closeActionChangeProjectModal,
    bulkActionModalsPortal,
  } = useBulkTimeEntryActionModals(
    entriesSelected,
    updateEntriesSelected,
    MetaTimesInfo.nonBillableReasons
  );

  const updateAndViewNext = () => {
    // logic for next user
    if (userList) {
      approveAndViewNextClicked = true;
      let newIndex: number = -1;
      if (nextPersonIndex > -1) {
        newIndex = nextPersonIndex;
      } else {
        newIndex = userList?.findIndex(
          (elem) => elem.userId === searchState.user.userId
        );
      }
      if (newIndex >= 0 && newIndex < userList?.length - 1) {
        actualNextIndex = newIndex + 1;
        updateEntriesSelected(3, null);
      } else if (newIndex === userList?.length - 1) {
        // last user in array
        actualNextIndex = -1;
        updateEntriesSelected(3, null);
      }
    }
  };

  return (
    <>
      {moreInfoModalPortal}
      <ModalPortal ref={modalRef}>
        <ModalTimeEntry
          title="Modal Entry"
          projectList={selectProjectOrWorkOrderAfterFilter}
          btnCancel={closeModal}
          reloadPage={refreshPage}
          type="formModal"
          editMode={true}
          entry={activeEntry}
          userIdParam={activeEntry.userId}
          holidays={holidays}
          deleteEntry={deleteEntry}
          timeEntriesList={timeEntriesListByUser}
          isQueryPage={true}
        />
      </ModalPortal>
      {deleteModalPortal}
      {bulkActionModalsPortal}

      <div className="content">
        <div className="mt-2 button-header">
          <Link
            className="back-button-container text-decoration-none"
            to={ROUTE_APPROVE_TIME_TIMESHEETS}
          >
            <BiLeftArrowAlt className="back-button-arrow" />
            <span className="back-button-text">Back to Approve time</span>
          </Link>
        </div>
        <CheckSkeleton
          isLoading={isLoading || !rolesLoaded}
          classes="w-50 fs-3"
        >
          <div className="d-flex flex-wrap mb-1">
            {searchState?.user && (
              <h2 className="flex-fill">
                {searchState.user.userName}{" "}
                <span>({getCorrectHoursType(searchState.hoursType)})</span>
              </h2>
            )}
          </div>
        </CheckSkeleton>
        <div className="flex-container flex-wrap">
          <div className="col-lg-4 col-12 d-inline-flex mb-3">
            <div className="col-4">From:</div>
            {searchState?.user && (
              <div className="col-7 fw-bold">{`${dateFormatEntryEdit(
                searchState.user.startDate
              )} - ${dateFormatEntryEdit(searchState.filters.endDate)}`}</div>
            )}
          </div>
          {listFiltersTouched &&
            listFiltersTouched.length > 0 &&
            listFiltersTouched.map((filter: any) => {
              return (
                <div
                  className="col-lg-4 col-12 d-inline-flex mb-3"
                  key={filter.name}
                >
                  <div className="col-4">{filter.name}:</div>
                  <div className="col-7 fw-bold">{filter.value}</div>
                </div>
              );
            })}
        </div>
        <SummaryTotals
          showTitle={false}
          totals={summary.totalHours}
          billable={summary.billableHours}
          financed={summary.financedHours}
          nonBillable={summary.nonBillableHours}
          internal={summary.internalHours}
          timeOff={summary.timeOffHours}
          isLoading={isLoading || !rolesLoaded}
          avoidCheckMissingHours={true}
        />
        <hr className="header-separator mt-4" />

        <div className="d-flex mt-3 justify-content-between">
          <div
            className="td-task-link mt-2"
            role="button"
            tabIndex={0}
            onClick={() => updateAllSelected()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                updateAllSelected();
              }
            }}
          >
            Select All
          </div>
          <div className="view-buttons">
            {Object.keys(timeEntriesList).length > 0 && (
              <ActionsButton
                disabled={
                  entriesSelected.length === 0 || isLoading || !rolesLoaded
                }
                entriesSelected={entriesSelected}
                showActionNonBillaleModal={showActionNonBillaleModal}
                showActionChangeDateModal={showActionChangeDateModal}
                showActionChangeProjectModal={showActionChangeProjectModal}
                updateEntriesSelected={updateEntriesSelected}
                activeUserId={activeUserId}
              />
            )}
            <button
              className="btn btn-primary ms-3"
              onClick={() => updateEntriesSelected(3, null)}
              disabled={
                entriesSelected.length === 0 ||
                isLoading ||
                !rolesLoaded ||
                entriesSelected.some((e) => e.userId === activeUserId) ||
                entriesSelected.some((e) => e.status === LOCKED) ||
                entriesSelected.some((e) => e.categoryId === 3) ||
                entriesSelected.some((e) => !e.canApprove) ||
                entriesSelected.some((e) => isClosed(e.entityStatus)) ||
                entriesSelected.some((e) => isProjectClosed(e))
              }
            >
              Approve
            </button>
            <button
              className="btn btn-primary ms-3"
              onClick={() => updateAndViewNext()}
              disabled={
                entriesSelected.length === 0 ||
                isLoading ||
                !rolesLoaded ||
                entriesSelected.some((e) => e.userId === activeUserId) ||
                entriesSelected.some((e) => e.status === LOCKED) ||
                entriesSelected.some((e) => e.categoryId === 3) ||
                entriesSelected.some((e) => !e.canApprove) ||
                entriesSelected.some((e) => isClosed(e.entityStatus)) ||
                entriesSelected.some((e) => isProjectClosed(e))
              }
            >
              Approve & view next person
            </button>
          </div>
        </div>

        {(isLoading || !rolesLoaded) && (
          <Spinner
            style={{
              marginLeft: "45%",
              marginTop: "10%",
            }}
          />
        )}

        {!isLoading &&
          rolesLoaded &&
          Object.keys(timeEntriesList).length > 0 &&
          Object.keys(timeEntriesList)
            .sort((a, b) => a.localeCompare(b))
            .map((keyName) => {
              let actualSummary = {
                total: 0,
                billable: 0,
                nonBillable: 0,
                internal: 0,
                timeOff: 0,
              };
              timeEntriesList[keyName].forEach((element) => {
                actualSummary[firstLetterToLower(element.timeEntryType)] +=
                  element.hours;
                actualSummary["total"] += element.hours;
              });
              return (
                <>
                  <div
                    className="d-flex mt-4 justify-content-between sticky-date sticky"
                    key={keyName + "-summary"}
                  >
                    <div className="left-side d-flex align-items-center">
                      <BiCalendar style={{ width: "20px", height: "20px" }} />
                      <div className="table-date ms-2">
                        {nameDayDateFormat(
                          timeEntriesList[keyName][0].entryDate
                        )}
                      </div>
                    </div>
                    <DayTotals daySummary={actualSummary} />
                  </div>

                  <table
                    className="table white-table-header-background mt-3 mb-5"
                    style={{ width: "100%" }}
                    key={"table-" + keyName}
                  >
                    <thead className="align-middle sticky-header-with-date sticky">
                      <tr>
                        <th className="text-center border-dark border-top fw-500 text-center py-1 column-5 small">
                          Select
                          <div className="d-flex justify-content-center">
                            <div
                              className="link-text mx-2"
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                updateSelectedByDay(
                                  timeEntriesList[keyName][0].entryDate,
                                  true
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  updateSelectedByDay(
                                    timeEntriesList[keyName][0].entryDate,
                                    true
                                  );
                                }
                              }}
                            >
                              All
                            </div>
                            /
                            <div
                              className="link-text mx-2"
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                updateSelectedByDay(
                                  timeEntriesList[keyName][0].entryDate,
                                  false
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  updateSelectedByDay(
                                    timeEntriesList[keyName][0].entryDate,
                                    false
                                  );
                                }
                              }}
                            >
                              None
                            </div>
                          </div>
                        </th>
                        <th className="border-dark border-top fw-500 py-0 ps-4 column-20">
                          Client / Project / Task
                        </th>
                        <th className="border-dark border-top fw-500 py-0 ps-4 column-15">
                          Hours / Task Type
                        </th>
                        <th className="border-dark border-top fw-500 py-0 ps-4 column-30">
                          Description
                        </th>
                        <th className="border-dark border-top fw-500 py-0 ps-4 column-15">
                          Billable/Price
                        </th>
                        <th className="border-dark border-top fw-500 py-0 column-5">
                          Status
                        </th>
                        <th className="border-dark border-top fw-500 py-0 ps-3 column-15">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeEntriesList[keyName].map(
                        (entry: TimeEntryApprovalTime) => {
                          return (
                            <tr
                              key={"row-" + entry.timeEntryId}
                              style={{ verticalAlign: "middle" }}
                            >
                              <td className="table-td text-center py-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={isSelected(entry)}
                                  disabled={entry.source !== THE_HUB}
                                  onChange={() => updatedSelected(entry)}
                                />
                              </td>
                              <td className="table-td ps-4 py-3 fw-500">
                                {entry.categoryId !== 3 && (
                                  <>
                                    <div className="td-small-text">
                                      {entry.clientName}
                                    </div>
                                    <ExternalLink
                                      className="td-task-link"
                                      to={navigateTo(entry)}
                                    >
                                      Project{" "}
                                      {entry.projectId
                                        ? entry.projectId
                                        : entry.entityId}{" "}
                                      - {entry.entityDescription}
                                    </ExternalLink>
                                  </>
                                )}
                                {entry.categoryId === 3 && (
                                  <>
                                    <div className="td-small-text">
                                      {entry.clientName}
                                    </div>
                                    <ExternalLink
                                      className="td-task-link"
                                      to={navigateTo(entry)}
                                    >
                                      Work Order {entry.entityId} -{" "}
                                      {entry.entityDescription}
                                    </ExternalLink>
                                  </>
                                )}
                                <div className="td-small-text mt-1">
                                  {getUserName(entry.userId)}
                                </div>
                              </td>
                              <td className="table-td ps-4 py-3 fw-500">
                                <div className="td-bold">
                                  {quantityFormat(entry.hours)}
                                </div>
                                <div className="td-text">{entry.taskType}</div>
                              </td>
                              <td className="table-td ps-4 py-3 fw-500">
                                <div className="td-text text-wrap">
                                  {decodeURIComponentSafe(entry.description)}
                                </div>
                              </td>
                              <td className="table-td ps-4 py-3 fw-500">
                                <div className="td-bold">
                                  {getTimeEntryTypeLabel(entry.timeEntryType)}
                                  {entry.timeEntryTypeId === 3
                                    ? "-Financed"
                                    : ""}
                                </div>
                                {entry.timeEntryType === BILLABLE && (
                                  <div className="td-small-text">
                                    {entry.userPriceLevel}
                                  </div>
                                )}
                                {nonBillableIds.includes(
                                  entry.timeEntryTypeId.toString().toLowerCase()
                                ) && (
                                  <div className="td-small-text">
                                    {entry.nonBillableReason}
                                  </div>
                                )}
                                <div className="td-text">
                                  ${quantityFormat(entry.price)}
                                </div>
                              </td>
                              <td className="table-td py-4 fw-500">
                                <div
                                  className={`td-status ${entry.status} ${
                                    entry.status === LOCKED ? "d-flex" : ""
                                  }`}
                                >
                                  {entry.status}
                                  {entry.status === LOCKED && (
                                    <BiLockAlt
                                      className="ms-1"
                                      style={{ width: "16px", height: "16px" }}
                                    />
                                  )}
                                </div>
                              </td>
                              <td className="table-td action-icons-td py-4">
                                <div className="d-flex flex-column">
                                  {(entry.timeEntrySourceId === 3 ||
                                    (entry.timeEntrySourceId === 2 &&
                                      !entry.sourceUrl)) && (
                                    <div className="entry-text-imported link-black">
                                      From {entry.source}
                                    </div>
                                  )}
                                  {entry.timeEntrySourceId === 2 &&
                                    entry.sourceUrl && (
                                      <Link
                                        to={entry.sourceUrl}
                                        className="entry-text-imported text-decoration-none other-links-hover"
                                      >
                                        From {entry.source}
                                      </Link>
                                    )}
                                  <div>
                                    <Tooltip
                                      title={<b>More Info</b>}
                                      placement="top"
                                      slots={{ transition: Zoom }}
                                      arrow
                                    >
                                      <span>
                                        <BiInfoCircle
                                          className={`icon-action ${
                                            !isLoading || !rolesLoaded
                                              ? ""
                                              : "disabled"
                                          }`}
                                          onClick={() => showMoreInfo(entry)}
                                        />
                                      </span>
                                    </Tooltip>
                                    <Tooltip
                                      title={<b>Edit</b>}
                                      placement="top"
                                      slots={{ transition: Zoom }}
                                      arrow
                                    >
                                      <span>
                                        <BiEditAlt
                                          className={`icon-action ${
                                            (!isLoading || !rolesLoaded) &&
                                            iconEditEnabled(
                                              entry,
                                              activeUserId,
                                              entry.userId,
                                              isSuperTimeAdmin,
                                              isSupervisorOrPO,
                                              isDeliveryTimeAdmin,
                                              false,
                                              previousWorkableDay,
                                              today,
                                              lastWorkableDay
                                            )
                                              ? ""
                                              : "disabled"
                                          }`}
                                          onClick={() =>
                                            (!isLoading || !rolesLoaded) &&
                                            iconEditEnabled(
                                              entry,
                                              activeUserId,
                                              entry.userId,
                                              isSuperTimeAdmin,
                                              isSupervisorOrPO,
                                              isDeliveryTimeAdmin,
                                              false,
                                              previousWorkableDay,
                                              today,
                                              lastWorkableDay
                                            )
                                              ? editEntry(entry)
                                              : undefined
                                          }
                                        />
                                      </span>
                                    </Tooltip>
                                    <Tooltip
                                      title={<b>Delete</b>}
                                      placement="top"
                                      slots={{ transition: Zoom }}
                                      arrow
                                    >
                                      <span>
                                        <BiTrash
                                          className={`icon-action ${
                                            (!isLoading || !rolesLoaded) &&
                                            iconDeleteEnabled(
                                              entry,
                                              activeUserId,
                                              entry.userId,
                                              isSuperTimeAdmin,
                                              isSupervisorOrPO,
                                              isDeliveryTimeAdmin,
                                              false,
                                              previousWorkableDay,
                                              today,
                                              lastWorkableDay
                                            )
                                              ? ""
                                              : "disabled"
                                          }`}
                                          onClick={() =>
                                            (!isLoading || !rolesLoaded) &&
                                            iconDeleteEnabled(
                                              entry,
                                              activeUserId,
                                              entry.userId,
                                              isSuperTimeAdmin,
                                              isSupervisorOrPO,
                                              isDeliveryTimeAdmin,
                                              false,
                                              previousWorkableDay,
                                              today,
                                              lastWorkableDay
                                            )
                                              ? deleteEntry(entry)
                                              : undefined
                                          }
                                        />
                                      </span>
                                    </Tooltip>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </>
              );
            })}

        <Footer />
      </div>
    </>
  );
};
